// =============================================================================
// GOUVERNANCE - Espace de pilotage CTO
// =============================================================================
// Accès : Icône ⚙ dans le header (Super Admin / Admin uniquement)
// Structure 3 menus + Dashboard racine :
//   • Les Gardiens   → Gamora / Groot / Gatekeeper / Nebula
//   • Configuration  → Config globale / Sécurité / Intégrations / Listes
//                      & Statuts / Documents / Workflows
//   • Opérations     → Tickets / Règles / Coûts / Audit / Base de Connaissance
// =============================================================================

import { useState, useEffect } from "react";
import {
  Ticket,
  Shield,
  GitBranch,
  Scale,
  Lock,
  Plug,
  Settings,
  FileText,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  Activity,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Users,
  Database,
  Server,
  List,
  DollarSign,
  Mail
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { useAuth } from "../../contexts/AuthContext";

// Import des widgets
import GouvTickets from "./widgets/GouvTickets";
import GouvGroot from "./widgets/GouvGroot";
import GouvGatekeeper from "./widgets/GouvGatekeeper";
import GouvSecurity from "./widgets/GouvSecurity";
// GouvIntegrations remplacé par MultiConnectionsManager (plus complet)
import MultiConnectionsManager from "../management/MultiConnectionsManager";
import GouvAudit from "./widgets/GouvAudit";
import GouvRules from "./widgets/GouvRules";
import GouvDashboard from "./widgets/GouvDashboard";
import GouvConfig from "./widgets/GouvConfig";
import GouvDocRequirements from "./widgets/GouvDocRequirements";
import GouvListesStatuts from "./widgets/GouvListesStatuts";
import GouvCosts from "./widgets/GouvCosts";
import GouvWorkflows from "./widgets/GouvWorkflows";
import GouvKnowledgeBase from "./widgets/GouvKnowledgeBase";
import GouvGamora from "./widgets/GouvGamora";
import GouvNebula from "./widgets/GouvNebula";
import GouvRocket from "./widgets/GouvRocket";
import GouvEmailConfig from "./widgets/GouvEmailConfig";
import GouvFindings from "./widgets/GouvFindings";

const API = process.env.REACT_APP_BACKEND_URL;

// Définition des 9 widgets (Dashboard ajouté en premier)
const GOVERNANCE_WIDGETS = [
  {
    id: "dashboard",
    label: "Dashboard",
    description: "Vue d'ensemble",
    icon: Activity,
    color: "text-emerald-400",
    bgColor: "bg-emerald-500/10",
    borderColor: "border-emerald-500/30",
    component: GouvDashboard
  },
  {
    id: "gamora",
    label: "Gamora",
    description: "Qualité des données",
    icon: Shield,
    color: "text-purple-400",
    bgColor: "bg-purple-500/10",
    borderColor: "border-purple-500/30",
    component: GouvGamora
  },
  {
    id: "costs",
    label: "Coûts",
    description: "Budget & APIs",
    icon: DollarSign,
    color: "text-emerald-400",
    bgColor: "bg-emerald-500/10",
    borderColor: "border-emerald-500/30",
    component: GouvCosts
  },
  {
    id: "tickets",
    label: "Tickets",
    description: "Dev & Business",
    icon: Ticket,
    color: "text-blue-400",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/30",
    component: GouvTickets
  },
  {
    id: "groot",
    label: "Groot",
    description: "Guardian",
    icon: Shield,
    color: "text-emerald-400",
    bgColor: "bg-emerald-500/10",
    borderColor: "border-emerald-500/30",
    component: GouvGroot
  },
  {
    id: "gatekeeper",
    label: "Gatekeeper",
    description: "CI / Tests",
    icon: GitBranch,
    color: "text-purple-400",
    bgColor: "bg-purple-500/10",
    borderColor: "border-purple-500/30",
    component: GouvGatekeeper
  },
  {
    id: "nebula",
    label: "Nebula",
    description: "Qualité runtime",
    icon: Activity,
    color: "text-blue-400",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/30",
    component: GouvNebula
  },
  {
    id: "rocket",
    label: "Rocket",
    description: "Sécurité (anti-régression)",
    icon: Shield,
    color: "text-red-400",
    bgColor: "bg-red-500/10",
    borderColor: "border-red-500/30",
    component: GouvRocket
  },
  {
    id: "findings",
    label: "Findings",
    description: "governance_findings",
    icon: AlertTriangle,
    color: "text-orange-400",
    bgColor: "bg-orange-500/10",
    borderColor: "border-orange-500/30",
    component: GouvFindings
  },
  {
    id: "rules",
    label: "Règles",
    description: "Policy Engine",
    icon: Scale,
    color: "text-amber-400",
    bgColor: "bg-amber-500/10",
    borderColor: "border-amber-500/30",
    component: GouvRules
  },
  {
    id: "documents",
    label: "Documents",
    description: "Exigences par transition",
    icon: FileText,
    color: "text-indigo-400",
    bgColor: "bg-indigo-500/10",
    borderColor: "border-indigo-500/30",
    component: GouvDocRequirements
  },
  {
    id: "statuts",
    label: "Listes & Statuts",
    description: "Configuration des listes",
    icon: List,
    color: "text-teal-400",
    bgColor: "bg-teal-500/10",
    borderColor: "border-teal-500/30",
    component: GouvListesStatuts
  },
  {
    id: "workflows",
    label: "Workflows",
    description: "Cycles de vie versionnés",
    icon: GitBranch,
    color: "text-purple-400",
    bgColor: "bg-purple-500/10",
    borderColor: "border-purple-500/30",
    component: GouvWorkflows
  },
  {
    id: "security",
    label: "Sécurité",
    description: "RBAC Global",
    icon: Lock,
    color: "text-red-400",
    bgColor: "bg-red-500/10",
    borderColor: "border-red-500/30",
    component: GouvSecurity
  },
  {
    id: "integrations",
    label: "Intégrations",
    description: "APIs & Connexions",
    icon: Plug,
    color: "text-cyan-400",
    bgColor: "bg-cyan-500/10",
    borderColor: "border-cyan-500/30",
    component: MultiConnectionsManager
  },
  {
    id: "config",
    label: "Configuration",
    description: "Globale",
    icon: Settings,
    color: "text-zinc-400",
    bgColor: "bg-zinc-500/10",
    borderColor: "border-zinc-500/30",
    component: GouvConfig
  },
  {
    id: "emails",
    label: "Emails & Notifications",
    description: "Templates + destinataires",
    icon: Mail,
    color: "text-cyan-400",
    bgColor: "bg-cyan-500/10",
    borderColor: "border-cyan-500/30",
    component: GouvEmailConfig
  },
  {
    id: "knowledge-base",
    label: "Base de Connaissance",
    description: "FAQ & Aide",
    icon: FileText,
    color: "text-indigo-400",
    bgColor: "bg-indigo-500/10",
    borderColor: "border-indigo-500/30",
    component: GouvKnowledgeBase
  },
  {
    id: "audit",
    label: "Audit",
    description: "Logs & Traces",
    icon: FileText,
    color: "text-orange-400",
    bgColor: "bg-orange-500/10",
    borderColor: "border-orange-500/30",
    component: GouvAudit
  }
];

// Regroupement par menu (Restructuration 2026-05-12)
// Dashboard reste en racine (pas de header) ; 3 sections sémantiques en dessous.
const GOVERNANCE_GROUPS = [
  {
    id: "root",
    label: null,
    widgetIds: ["dashboard"]
  },
  {
    id: "gardiens",
    label: "Les Gardiens",
    icon: Shield,
    widgetIds: ["gamora", "groot", "gatekeeper", "nebula", "rocket", "findings"]
  },
  {
    id: "config",
    label: "Configuration",
    icon: Settings,
    widgetIds: ["config", "security", "integrations", "emails", "statuts", "documents", "workflows"]
  },
  {
    id: "ops",
    label: "Opérations",
    icon: Activity,
    widgetIds: ["tickets", "rules", "costs", "audit", "knowledge-base"]
  }
];

const WIDGETS_BY_ID = Object.fromEntries(GOVERNANCE_WIDGETS.map(w => [w.id, w]));

export default function Governance({ onClose, currentUser, currentTenant, tenants = [], initialWidget }) {
  const { user } = useAuth();
  const effectiveUser = currentUser || user;
  const [activeWidget, setActiveWidget] = useState(initialWidget || "dashboard"); // Dashboard par défaut
  // Sections collapse/expand persistées (gardiens / config / ops). Le groupe "root"
  // (Dashboard) reste toujours visible — pas de label, pas collapsable.
  const COLLAPSE_KEY = "gouv:collapsedGroups";
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_KEY);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });
  const toggleGroup = (groupId) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
      try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...next])); } catch {}
      return next;
    });
  };
  const [stats, setStats] = useState({
    tickets: { total: 0, in_progress: 0, blocked: 0 },
    groot: { can_deploy: null, violations: 0 },
    gatekeeper: { last_scan: null, e2e_passed: null }
  });

  // Charger les stats globales
  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      // Stats tickets
      const ticketsRes = await fetch(`${API}/api/oh/tickets/stats/overview`);
      if (ticketsRes.ok) {
        const data = await ticketsRes.json();
        setStats(prev => ({
          ...prev,
          tickets: {
            total: data.total || 0,
            in_progress: data.active || 0,
            blocked: data.blocked || 0
          }
        }));
      }

      // Stats gatekeeper
      const gatekeeperRes = await fetch(`${API}/api/groot/gatekeeper/status`);
      if (gatekeeperRes.ok) {
        const data = await gatekeeperRes.json();
        setStats(prev => ({
          ...prev,
          groot: {
            can_deploy: data.latest_scan?.can_deploy,
            violations: data.latest_scan?.violations_critical || 0
          },
          gatekeeper: {
            last_scan: data.latest_scan?.completed_at,
            e2e_passed: data.latest_scan?.e2e_passed
          }
        }));
      }
    } catch (err) {
      console.error("Erreur chargement stats gouvernance:", err);
    }
  };

  const activeWidgetConfig = GOVERNANCE_WIDGETS.find(w => w.id === activeWidget);
  const ActiveComponent = activeWidgetConfig?.component;

  // Sidebar rendering helper (Restructuration 2026-05-12)
  // Groupe le rendu par section (Gardiens / Configuration / Opérations).
  // Mobile: liste scrollable verticale plate (pas d'onglets horizontaux).
  // Desktop: sidebar verticale fixe, sections séparées par leur header.
  const renderNavItem = (widget) => {
    if (!widget) return null;
    const isActive = activeWidget === widget.id;
    return (
      <button
        key={widget.id}
        onClick={() => setActiveWidget(widget.id)}
        data-testid={`gouv-nav-${widget.id}`}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left ${
          isActive
            ? `${widget.bgColor} ${widget.borderColor} border shadow`
            : 'hover:bg-zinc-800/50 border border-transparent'
        }`}
      >
        <div className={`w-9 h-9 rounded-lg ${widget.bgColor} flex items-center justify-center shrink-0`}>
          <widget.icon className={`w-4 h-4 ${widget.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium truncate ${isActive ? widget.color : 'text-white'}`}>
              {widget.label}
            </span>
            {widget.id === "tickets" && stats.tickets.blocked > 0 && (
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                {stats.tickets.blocked}
              </Badge>
            )}
          </div>
          <p className="text-xs text-zinc-500 truncate">{widget.description}</p>
        </div>
        {isActive && <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />}
      </button>
    );
  };

  const renderGroup = (group) => {
    const widgets = group.widgetIds.map(id => WIDGETS_BY_ID[id]).filter(Boolean);
    if (widgets.length === 0) return null;
    const collapsible = !!group.label;
    const isCollapsed = collapsible && collapsedGroups.has(group.id);
    return (
      <div key={group.id} className="space-y-1">
        {group.label && (
          <button
            type="button"
            onClick={() => toggleGroup(group.id)}
            aria-expanded={!isCollapsed}
            data-testid={`gouv-group-toggle-${group.id}`}
            className="w-full px-3 pt-3 pb-1 flex items-center gap-2 group hover:text-zinc-300 transition-colors"
          >
            {isCollapsed ? (
              <ChevronRight className="w-3.5 h-3.5 text-zinc-500 group-hover:text-zinc-300 transition-transform" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5 text-zinc-500 group-hover:text-zinc-300 transition-transform" />
            )}
            {group.icon && (
              <group.icon className="w-3.5 h-3.5 text-zinc-500 group-hover:text-zinc-300" />
            )}
            <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 group-hover:text-zinc-300">
              {group.label}
            </span>
            <span className="ml-auto text-[10px] text-zinc-600">{widgets.length}</span>
          </button>
        )}
        {!isCollapsed && widgets.map(renderNavItem)}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header Gouvernance */}
      <div className="bg-zinc-900/50 border-b border-zinc-800 px-4 md:px-6 py-3 md:py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 md:gap-4 min-w-0">
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-zinc-800 transition-colors shrink-0"
              data-testid="gouv-close"
            >
              <ArrowLeft className="w-5 h-5 text-zinc-400" />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg md:text-xl font-bold text-white flex items-center gap-2">
                <Settings className="w-5 h-5 text-zinc-400 shrink-0" />
                <span className="truncate">Gouvernance</span>
              </h1>
              <p className="text-xs md:text-sm text-zinc-500 truncate">Pilotage technique CTO</p>
            </div>
          </div>

          {/* Indicateurs rapides — masqués sur mobile */}
          <div className="hidden md:flex items-center gap-4">
            {/* Can Deploy */}
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
              stats.groot.can_deploy === true 
                ? 'bg-emerald-500/10 border border-emerald-500/30'
                : stats.groot.can_deploy === false
                  ? 'bg-red-500/10 border border-red-500/30'
                  : 'bg-zinc-800 border border-zinc-700'
            }`}>
              {stats.groot.can_deploy === true ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              ) : stats.groot.can_deploy === false ? (
                <XCircle className="w-4 h-4 text-red-400" />
              ) : (
                <Clock className="w-4 h-4 text-zinc-400" />
              )}
              <span className={`text-sm font-medium ${
                stats.groot.can_deploy === true 
                  ? 'text-emerald-400'
                  : stats.groot.can_deploy === false
                    ? 'text-red-400'
                    : 'text-zinc-400'
              }`}>
                {stats.groot.can_deploy === true 
                  ? 'Deploy OK'
                  : stats.groot.can_deploy === false
                    ? 'Deploy Bloqué'
                    : 'Non scanné'}
              </span>
            </div>

            {/* Violations */}
            {stats.groot.violations > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-sm font-medium text-red-400">
                  {stats.groot.violations} violations
                </span>
              </div>
            )}

            {/* Tickets en cours */}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700">
              <Ticket className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-zinc-300">
                {stats.tickets.in_progress} en cours
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Layout principal : Sidebar + Contenu (stack en mobile, side-by-side en md+) */}
      <div className="flex flex-col md:flex-row">
        {/* Sidebar groupée — liste scrollable verticale (mobile ET desktop) */}
        <aside className="md:w-72 bg-zinc-900/30 border-b md:border-b-0 md:border-r border-zinc-800 md:min-h-[calc(100vh-73px)] md:max-h-[calc(100vh-73px)] md:overflow-y-auto">
          <nav className="p-2 md:p-3 space-y-2 max-h-[40vh] md:max-h-none overflow-y-auto">
            {GOVERNANCE_GROUPS.map(renderGroup)}
          </nav>
        </aside>

        {/* Contenu du widget actif */}
        <main className="flex-1 p-3 md:p-6 overflow-auto min-w-0">
          {ActiveComponent ? (
            // Passer les props nécessaires selon le widget
            (["security", "config", "emails"].includes(activeWidget)) ? (
              <ActiveComponent
                currentUser={effectiveUser}
                currentTenant={currentTenant}
                tenants={tenants}
                onRefreshStats={loadStats}
              />
            ) : (
              <ActiveComponent onRefreshStats={loadStats} />
            )
          ) : (
            <div className="flex items-center justify-center h-64">
              <p className="text-zinc-500">Widget non disponible</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
